import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary,
  Grid, NativeSelect, Paper, Typography, Tabs, Tab
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MaterialTable from 'material-table'
import { toast } from 'react-toastify'

import api from '../../../../services/api'
import { materialTableLocalizationPtBR } from '../../../../services/materialTableLocalization'

const TIPO_ARQUIVO_OPTIONS = [
  { value: 'REM', label: 'REM (envio)' },
  { value: 'RET', label: 'RET (retorno)' },
]

// Valores correspondem aos NOMES dos enums C# (enumLayoutCnabTipoRegistro etc).
// A API serializa enums como string nominal, entao usamos string como chave do
// lookup do MaterialTable.
const TIPO_REGISTRO_OPTIONS = [
  { value: 'HeaderArquivo', label: 'Header Arquivo' },
  { value: 'HeaderLote', label: 'Header Lote' },
  { value: 'DetalheSegmento', label: 'Detalhe (Segmento)' },
  { value: 'TrailerLote', label: 'Trailer Lote' },
  { value: 'TrailerArquivo', label: 'Trailer Arquivo' },
]

const SEGMENTO_OPTIONS = [
  { value: 'Nenhum', label: '—' },
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
]

const FILTRO_PGTO_OPTIONS = [
  { value: 'Ambos', label: 'Ambos' },
  { value: 'TED', label: 'TED' },
  { value: 'PIX', label: 'PIX' },
]

const TIPO_DADO_OPTIONS = [
  { value: 'Numerico', label: 'Numérico' },
  { value: 'Alfanumerico', label: 'Alfanumérico' },
]

const STATUS_OPTIONS = [
  { value: 'Ativo', label: 'Ativo' },
  { value: 'Inativo', label: 'Inativo' },
]

// Mantém em sincronia com enumCnabVariavelSistema (LayoutCnabRegistros.cs).
const VARIAVEL_OPTIONS = [
  'Nenhuma', 'EMPRESA_CNPJ', 'EMPRESA_AGENCIA', 'EMPRESA_CONTA', 'EMPRESA_DAC', 'EMPRESA_NOME',
  'DATA_GERACAO', 'HORA_GERACAO', 'NUMERO_LOTE', 'NUMERO_SEQUENCIAL', 'CAMARA',
  'FAVORECIDO_BANCO', 'FAVORECIDO_AGENCIA_CONTA', 'FAVORECIDO_NOME', 'SEU_NUMERO',
  'DATA_PAGAMENTO', 'VALOR_PAGAMENTO', 'FAVORECIDO_CNPJ', 'TIPO_CHAVE_PIX', 'CHAVE_PIX',
  'TXID', 'TOTAL_REGISTROS_LOTE', 'TOTAL_VALOR_LOTE', 'TOTAL_LOTES',
  'TOTAL_REGISTROS_ARQUIVO', 'SEQUENCIAL_ARQUIVO', 'FINALIDADE_TED', 'IDENTIFICACAO_TRANSF'
].map((v) => ({ value: v, label: v }))

const labelByValue = (opts) => opts.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {})

const TIPO_REGISTRO_LABEL = labelByValue(TIPO_REGISTRO_OPTIONS)
const SEGMENTO_LABEL = labelByValue(SEGMENTO_OPTIONS)
const FILTRO_PGTO_LABEL = labelByValue(FILTRO_PGTO_OPTIONS)
const TIPO_DADO_LABEL = labelByValue(TIPO_DADO_OPTIONS)
const STATUS_LABEL = labelByValue(STATUS_OPTIONS)

/**
 * Lookup map pro inline edit do material-table (espera { valor: 'rótulo' }).
 */
const lookupFrom = (opts) => opts.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {})

export default function LayoutCnabAccordion ({ bancoRemessaId, readOnly, classes }) {
  const [tipoArquivo, setTipoArquivo] = useState('REM')
  const [campos, setCampos] = useState([])
  const [loading, setLoading] = useState(false)
  const [aplicandoSeed, setAplicandoSeed] = useState(false)
  // Contador que incrementa a cada fetch — usado como sufixo do key da
  // MaterialTable para forcar remount limpo (evita "linhas-fantasma" do estado
  // interno do material-table v1 quando o backend retorna lista vazia).
  const [refreshTick, setRefreshTick] = useState(0)

  const isPersisted = bancoRemessaId > 0

  const fetchCampos = useCallback(async () => {
    if (!isPersisted) return
    setLoading(true)
    try {
      const res = await api.get(`BancosRemessas/${bancoRemessaId}/LayoutCnab?tipoArquivo=${tipoArquivo}`)
      const raw = res?.data?.resposta
      const lista = Array.isArray(raw) ? raw.filter((r) => r && r.layoutCnabRegistroId > 0) : []
      setCampos(lista)
      setRefreshTick((t) => t + 1)
    } catch (err) {
      toast.error('Erro ao carregar layout CNAB.')
      setCampos([])
      setRefreshTick((t) => t + 1)
    } finally {
      setLoading(false)
    }
  }, [bancoRemessaId, tipoArquivo, isPersisted])

  useEffect(() => {
    fetchCampos()
  }, [fetchCampos])

  const handleAplicarSeed = async () => {
    if (!isPersisted) return
    if (campos.length > 0) {
      toast.warn('Remova todos os campos antes de aplicar o seed padrão.')
      return
    }
    if (!window.confirm('Aplicar o layout padrão Itaú 085? Isso cria 146+ campos para REM e RET.')) return
    setAplicandoSeed(true)
    try {
      const res = await api.post(`BancosRemessas/${bancoRemessaId}/LayoutCnab/AplicarSeedItau085`)
      if (res?.data?.status === 'Sucesso') {
        toast.success('Layout Itaú 085 aplicado.')
        await fetchCampos()
      } else {
        const erros = res?.data?.resposta || []
        if (erros.length > 0) erros.forEach((e) => toast.error(e.message))
        else toast.error('Erro ao aplicar seed.')
      }
    } catch (err) {
      const msg = err?.response?.data?.resposta?.[0]?.message
      toast.error(msg || 'Erro ao aplicar seed.')
    } finally {
      setAplicandoSeed(false)
    }
  }

  const handleAdd = async (novo) => {
    try {
      const payload = {
        bancoRemessaId,
        tipoArquivo,
        tipoRegistro: novo.tipoRegistro,
        segmento: novo.segmento || 'Nenhum',
        filtroPagamento: novo.filtroPagamento || 'Ambos',
        posicao: Number(novo.posicao) || 0,
        tamanho: Number(novo.tamanho) || 0,
        tipoDado: novo.tipoDado || 'Alfanumerico',
        variavelSistema: novo.variavelSistema || 'Nenhuma',
        valorFixo: novo.valorFixo || '',
        ordem: Number(novo.ordem) || 0,
        status: novo.status || 'Ativo',
        descricao: novo.descricao || '',
      }
      const res = await api.post(`BancosRemessas/${bancoRemessaId}/LayoutCnab`, payload)
      if (res?.data?.status === 'Sucesso') {
        toast.success('Campo adicionado.')
        await fetchCampos()
      } else {
        const erros = res?.data?.resposta || []
        if (erros.length > 0) erros.forEach((e) => toast.error(e.message))
        else toast.error('Erro ao adicionar.')
        // Rejeita para o MaterialTable nao manter a linha "fantasma" sem ID no estado interno.
        throw new Error('add-failed')
      }
    } catch (err) {
      if (err?.message !== 'add-failed') {
        const msg = err?.response?.data?.resposta?.[0]?.message
        toast.error(msg || 'Erro ao adicionar.')
      }
      throw err
    }
  }

  const handleUpdate = async (atualizado, original) => {
    if (!original?.layoutCnabRegistroId) {
      toast.warn('Esta linha ainda não foi salva. Use o botão de cadastrar (+) e preencha antes de editar.')
      await fetchCampos()
      return
    }
    try {
      const payload = {
        layoutCnabRegistroId: original.layoutCnabRegistroId,
        bancoRemessaId,
        tipoArquivo,
        tipoRegistro: atualizado.tipoRegistro,
        segmento: atualizado.segmento || 'Nenhum',
        filtroPagamento: atualizado.filtroPagamento || 'Ambos',
        posicao: Number(atualizado.posicao) || 0,
        tamanho: Number(atualizado.tamanho) || 0,
        tipoDado: atualizado.tipoDado,
        variavelSistema: atualizado.variavelSistema || 'Nenhuma',
        valorFixo: atualizado.valorFixo || '',
        ordem: Number(atualizado.ordem) || 0,
        status: atualizado.status || 'Ativo',
        descricao: atualizado.descricao || '',
        dataAtualizacao: original.dataAtualizacao,
      }
      const res = await api.put(`BancosRemessas/${bancoRemessaId}/LayoutCnab/${original.layoutCnabRegistroId}`, payload)
      if (res?.data?.status === 'Sucesso') {
        toast.success('Campo atualizado.')
        await fetchCampos()
      } else {
        const erros = res?.data?.resposta || []
        if (erros.length > 0) erros.forEach((e) => toast.error(e.message))
        else toast.error('Erro ao atualizar.')
      }
    } catch (err) {
      const msg = err?.response?.data?.resposta?.[0]?.message
      toast.error(msg || 'Erro ao atualizar.')
    }
  }

  const handleDelete = async (row) => {
    if (!row?.layoutCnabRegistroId) {
      toast.warn('Esta linha não tem ID — provavelmente está em modo de edição inline sem ter sido salva. Atualizando a lista.')
      await fetchCampos()
      return
    }
    try {
      const res = await api.delete(`BancosRemessas/${bancoRemessaId}/LayoutCnab/${row.layoutCnabRegistroId}`)
      if (res?.data?.status === 'Sucesso') {
        toast.success('Campo removido.')
        await fetchCampos()
      } else {
        toast.error('Erro ao remover.')
      }
    } catch (err) {
      toast.error('Erro ao remover.')
    }
  }

  // Agrupamento visual: soma de tamanhos por (TipoRegistro, Segmento, FiltroPagamento).
  // Cada grupo deve fechar em 240 chars na linha CNAB.
  const resumoCobertura = useMemo(() => {
    const grupos = new Map()
    for (const c of campos) {
      const chave = `${c.tipoRegistro}|${c.segmento}|${c.filtroPagamento}`
      const acc = grupos.get(chave) || {
        tipoRegistro: c.tipoRegistro, segmento: c.segmento, filtroPagamento: c.filtroPagamento,
        qtd: 0, somaTam: 0, ultimaPosFim: 0,
      }
      acc.qtd += 1
      acc.somaTam += c.tamanho
      const fim = c.posicao + c.tamanho - 1
      if (fim > acc.ultimaPosFim) acc.ultimaPosFim = fim
      grupos.set(chave, acc)
    }
    return Array.from(grupos.values()).sort((a, b) => a.tipoRegistro - b.tipoRegistro)
  }, [campos])

  const colunas = [
    { title: 'Ordem', field: 'ordem', type: 'numeric', width: 70 },
    {
      title: 'Tipo Registro', field: 'tipoRegistro', lookup: lookupFrom(TIPO_REGISTRO_OPTIONS),
      initialEditValue: 'DetalheSegmento', width: 140,
    },
    { title: 'Seg.', field: 'segmento', lookup: lookupFrom(SEGMENTO_OPTIONS), initialEditValue: 'Nenhum', width: 80 },
    {
      title: 'Filtro', field: 'filtroPagamento', lookup: lookupFrom(FILTRO_PGTO_OPTIONS),
      initialEditValue: 'Ambos', width: 90,
    },
    { title: 'Posição', field: 'posicao', type: 'numeric', width: 80 },
    { title: 'Tam.', field: 'tamanho', type: 'numeric', width: 70 },
    {
      title: 'Tipo Dado', field: 'tipoDado', lookup: lookupFrom(TIPO_DADO_OPTIONS),
      initialEditValue: 'Alfanumerico', width: 110,
    },
    {
      title: 'Variável Sistema', field: 'variavelSistema', lookup: lookupFrom(VARIAVEL_OPTIONS),
      initialEditValue: 'Nenhuma', width: 180,
    },
    { title: 'Valor Fixo', field: 'valorFixo', width: 120 },
    {
      title: 'Status', field: 'status', lookup: lookupFrom(STATUS_OPTIONS),
      initialEditValue: 'Ativo', width: 90,
    },
    { title: 'Descrição', field: 'descricao' },
  ]

  if (!isPersisted) {
    return (
      <Accordion className={classes.accordionRoot}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.summary}>
          <Typography className={classes.heading}>Layout CNAB</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <Typography variant="body2">
            Salve o banco primeiro para configurar o layout CNAB.
          </Typography>
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <Accordion className={classes.accordionRoot}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.summary}>
        <Typography className={classes.heading}>
          Layout CNAB&nbsp;
          <span style={{ fontWeight: 400, fontSize: '0.85em', color: '#666' }}>
            ({campos.length} campos em {tipoArquivo})
          </span>
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Tabs
              value={tipoArquivo}
              onChange={(_, v) => setTipoArquivo(v)}
              textColor="primary"
              indicatorColor="primary"
            >
              {TIPO_ARQUIVO_OPTIONS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>
          </Grid>

          <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#555' }}>
                <strong>Cobertura por registro/segmento:</strong>{' '}
                {resumoCobertura.length === 0 ? <em>nenhum campo cadastrado</em> : null}
                {resumoCobertura.map((g) => (
                  <span
                    key={`${g.tipoRegistro}|${g.segmento}|${g.filtroPagamento}`}
                    style={{
                      marginRight: 12,
                      color: g.ultimaPosFim === 240 ? '#2E7D32' : (g.ultimaPosFim > 240 ? '#C62828' : '#FB8832'),
                    }}
                    title={`Soma tamanhos: ${g.somaTam}; ultima posicao fim: ${g.ultimaPosFim}/240`}
                  >
                    {TIPO_REGISTRO_LABEL[g.tipoRegistro] || g.tipoRegistro}
                    {g.segmento !== 'Nenhum' ? `/${g.segmento}` : ''}
                    {g.filtroPagamento !== 'Ambos' ? `/${g.filtroPagamento}` : ''}
                    : {g.qtd} campos ({g.ultimaPosFim}/240)
                  </span>
                ))}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleAplicarSeed}
                  disabled={aplicandoSeed || campos.length > 0}
                  style={{
                    background: campos.length > 0 ? '#9CB7C4' : '#026894',
                    border: 'none', color: '#fff', padding: '6px 14px',
                    borderRadius: 2, fontWeight: 'bold', fontSize: 13,
                    cursor: campos.length > 0 ? 'not-allowed' : 'pointer',
                  }}
                  title={campos.length > 0 ? 'Remova os campos atuais para aplicar o seed' : 'Popula o layout completo Itau 085'}
                >
                  {aplicandoSeed ? 'Aplicando...' : 'Aplicar layout padrão Itaú 085'}
                </button>
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <MaterialTable
              key={`${tipoArquivo}-${campos.length}-${refreshTick}`}
              isLoading={loading}
              columns={colunas}
              data={campos}
              title={`Campos do layout ${tipoArquivo}`}
              options={{
                pageSize: 25,
                pageSizeOptions: [25, 50, 100, 200],
                addRowPosition: 'first',
                actionsColumnIndex: -1,
                headerStyle: { backgroundColor: '#F5F5F5', fontSize: 13 },
                rowStyle: { fontSize: 13 },
                search: true,
                searchFieldAlignment: 'left',
              }}
              localization={{
                ...materialTableLocalizationPtBR,
                body: {
                  ...materialTableLocalizationPtBR.body,
                  emptyDataSourceMessage: 'Nenhum campo cadastrado.',
                },
              }}
              editable={readOnly ? undefined : {
                onRowAdd: (novo) => handleAdd(novo),
                onRowUpdate: (atual, original) => handleUpdate(atual, original),
                onRowDelete: (row) => handleDelete(row),
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}
